/**
 * Catalog data: dynamic category tree builder, price groups, and swatch fallbacks.
 *
 * The CATEGORY_TREE is NO LONGER hardcoded — use buildCategoryTree(products)
 * to construct it at runtime from actual DB data. This makes the system modular:
 * when new categories are added to the database, the UI auto-updates.
 */

// ─── Swatch URL helper ────────────────────────────────────
import config from "../config/app.config"
const SWATCH_BASE = `${config.API_URL}files/${config.APP_NAME}/category-swatches`
const SWATCH_KEY = config.APP_API_KEY
const sw = (pg, filename) => `${SWATCH_BASE}/${pg}/${filename}.jpeg?api_key=${SWATCH_KEY}&v=2`

// ─── Color families: truly-shared swatches across boxes + specials ──────
// Same family = same physical color palette. Editing the canonical box group's
// swatch folder auto-updates everywhere that family is used (specials, lustre
// worktops, etc.). See PLAN-ELITON-FEEDBACK.md feature #3.
//
// Family table — keep in lock-step with backend reality. Special codes are
// what the DB actually stores on Specials' variants.finish_code (E1-E7,
// confirmed in May 2026 by sampling production data).
export const COLOR_FAMILIES = {
  FAM_1: { canonical: "1M", boxes: ["1M"],                       specials: ["E1"] },
  FAM_2: { canonical: "2P", boxes: ["2P"],                       specials: ["E2"] },
  FAM_3: { canonical: "3S", boxes: ["3S"],                       specials: ["E3"] },
  FAM_4: { canonical: "4C", boxes: ["4C"],                       specials: ["E4"] },
  FAM_5: { canonical: "5L", boxes: ["5L", "6L", "7T"],           specials: ["E5"] },
  FAM_6: { canonical: "6D", boxes: ["6D", "8D", "9H", "9M"],     specials: ["E6"] },
  FAM_7: { canonical: "7G", boxes: ["7G", "8G"],                 specials: ["E7"] },
}

// Reverse index built once at module load
const _codeToFamily = (() => {
  const map = {}
  for (const [famId, fam] of Object.entries(COLOR_FAMILIES)) {
    for (const code of [...fam.boxes, ...fam.specials]) map[code] = famId
  }
  return map
})()

/**
 * Resolve any price-group code (box or special) to its color-family id.
 * Returns null if the code isn't part of a family (e.g. 10A aluminum,
 * worktop codes P1/P2/P3 which have their own palette).
 */
export const getColorFamily = (code) => (code ? _codeToFamily[code] || null : null)

/**
 * Resolve a code to the canonical box code whose swatch folder contains the
 * actual files for its family. E5 → 5L, E6 → 6D, 8G → 7G, etc. Codes outside
 * any family return themselves unchanged.
 */
export const getCanonicalSwatchCode = (code) => {
  const fam = getColorFamily(code)
  return fam ? COLOR_FAMILIES[fam].canonical : code
}

// Special-only codes that should appear in the Specials picker but never as a box choice.
export const SPECIAL_PRICE_GROUPS = Object.values(COLOR_FAMILIES).flatMap(f => f.specials)

// ─── SKU prefix → Price Group mapping ────────────────────
// SKU format: Q[XX][YY].[product_code]  where XX = price tier code
export const SKU_TO_PRICE_GROUP = {
  "11": "1M",
  "13": "1M",
  "23": "2P",
  "25": "2P",
  "51": "5L",
  "52": "5L",
  "61": "6D",
  "63": "6L",
  "81": "8D",
  "93": "9M",
}

/**
 * Extract price group code from a product SKU.
 * SKU pattern: Q[XX][YY].[code] → XX maps to price group via SKU_TO_PRICE_GROUP.
 */
export const getPriceGroupFromSku = (sku) => {
  if (!sku || sku.length < 3 || sku[0] !== "Q") return null
  const prefix = sku.substring(1, 3)
  return SKU_TO_PRICE_GROUP[prefix] || null
}

/**
 * Get all SKU prefixes that map to a given price group code.
 * E.g. "1M" → ["11", "13"]
 */
export const getSkuPrefixesForGroup = (pgCode) => {
  return Object.entries(SKU_TO_PRICE_GROUP)
    .filter(([, pg]) => pg === pgCode)
    .map(([prefix]) => prefix)
}

/**
 * Build category tree dynamically from product data.
 * Parses the JSON category arrays and constructs a nested tree.
 *
 * @param {Array} products - Array of product objects with `category` field (JSON string)
 * @returns {Object} Nested tree: { cat1: { cat2: { cat3: { cat4: {} } } } }
 *
 * Category arrays look like:
 *   ["Cabinet", "Base", "Gola", "Drawer", "Blum", "Woodgrain", "B2I-BSG"]
 *   ["Accessories", "Handles", "Double", "BELT"]
 *   ["Special", "Cover-Panel", "Side-Panel", "Base", "SPB"]
 *
 * The last element is typically the model code — we stop before it.
 * Known leaf indicators: Woodgrain, Unicolor, MDF, PRFS (kasoma values)
 */
const KASOMA_VALUES = new Set(["Woodgrain", "Unicolor", "MDF", "PRFS"])

export const buildCategoryTree = (products) => {
  const tree = {}

  for (const product of products) {
    if (!product.category) continue
    let cats
    try {
      cats = JSON.parse(product.category)
      if (!Array.isArray(cats) || cats.length < 2) continue
    } catch {
      continue
    }

    // Walk the category array and build tree nodes
    // Stop before kasoma values and model codes (last element)
    let node = tree
    for (let i = 0; i < cats.length; i++) {
      const val = cats[i]
      // Stop at kasoma values — don't include in the navigation tree
      if (KASOMA_VALUES.has(val)) break
      // Stop at last element (model code like BNR, KNR, B2I-BSG)
      if (i === cats.length - 1) break
      // Also stop if next element is kasoma or last
      if (i === cats.length - 2 && KASOMA_VALUES.has(cats[i + 1])) break

      if (!node[val]) node[val] = {}
      node = node[val]
    }
  }

  return tree
}

/**
 * Legacy CATEGORY_TREE export — kept empty for backward compatibility.
 * Components should migrate to use the dynamic tree from useDynamicCatalog.
 */
export const CATEGORY_TREE = {}

// ─── Shared palettes (used across multiple price groups) ──────
const LACQUER_COLORS = [
  { label: "173 64",          swatchUrl: sw("5L", "173-64") },
  { label: "43001",           swatchUrl: sw("5L", "43001") },
  { label: "43004",           swatchUrl: sw("5L", "43004") },
  { label: "44 N5",           swatchUrl: sw("5L", "44-N5") },
  { label: "54 N3",           swatchUrl: sw("5L", "54-N3") },
  { label: "F 167",           swatchUrl: sw("5L", "F-167") },
  { label: "F 157",           swatchUrl: sw("5L", "F-157") },
  { label: "F 200",           swatchUrl: sw("5L", "F-200") },
  { label: "H 157",           swatchUrl: sw("5L", "H-157") },
  { label: "J 155",           swatchUrl: sw("5L", "J-155") },
  { label: "L 168",           swatchUrl: sw("5L", "L-168") },
  { label: "X 137",           swatchUrl: sw("5L", "X-137") },
  { label: "ΜΑΥΡΟ",           swatchUrl: sw("5L", "MAVRO") },
  { label: "ΛΕΥΚΟ",           swatchUrl: sw("5L", "LEFKO") },
  { label: "X 120",           swatchUrl: sw("5L", "X-120") },
  { label: "001W-3",          swatchUrl: sw("5L", "001W-3") },
  { label: "067 N1",          swatchUrl: sw("5L", "067-N1") },
  { label: "067 N2",          swatchUrl: sw("5L", "067-N2") },
  { label: "88 R5",           swatchUrl: sw("5L", "88-R5") },
  { label: "45 N3",           swatchUrl: sw("5L", "45-N3") },
  { label: "89 R6",           swatchUrl: sw("5L", "89-R6") },
  { label: "170 G6",          swatchUrl: sw("5L", "170-G6") },
  { label: "S 1005 Y20R",     swatchUrl: sw("5L", "S-1005-Y20R") },
]

const WOOD_SWATCHES = [
  { label: "WOOD 101", swatchUrl: sw("6D", "WOOD-101") },
  { label: "WOOD 102", swatchUrl: sw("6D", "WOOD-102") },
  { label: "WOOD 103", swatchUrl: sw("6D", "WOOD-103") },
  { label: "WOOD 104", swatchUrl: sw("6D", "WOOD-104") },
  { label: "WOOD 105", swatchUrl: sw("6D", "WOOD-105") },
  { label: "WOOD 106", swatchUrl: sw("6D", "WOOD-106") },
  { label: "WOOD 107", swatchUrl: sw("6D", "WOOD-107") },
  { label: "WOOD 108", swatchUrl: sw("6D", "WOOD-108") },
  { label: "WOOD 109", swatchUrl: sw("6D", "WOOD-109") },
  { label: "WOOD 110", swatchUrl: sw("6D", "WOOD-110") },
  { label: "WOOD 111", swatchUrl: sw("6D", "WOOD-111") },
  { label: "WOOD 112", swatchUrl: sw("6D", "WOOD-112") },
  { label: "WOOD 113", swatchUrl: sw("6D", "WOOD-113") },
  { label: "WOOD 114", swatchUrl: sw("6D", "WOOD-114") },
  { label: "WOOD 115", swatchUrl: sw("6D", "WOOD-115") },
  { label: "WOOD 116", swatchUrl: sw("6D", "WOOD-116") },
  { label: "WOOD 117", swatchUrl: sw("6D", "WOOD-117") },
  { label: "WOOD 118", swatchUrl: sw("6D", "WOOD-118") },
  { label: "WOOD 119", swatchUrl: sw("6D", "WOOD-119") },
  { label: "WOOD 120", swatchUrl: sw("6D", "WOOD-120") },
  { label: "WOOD 121", swatchUrl: sw("6D", "WOOD-121") },
  { label: "WOOD 122", swatchUrl: sw("6D", "WOOD-122") },
  { label: "WOOD 123", swatchUrl: sw("6D", "WOOD-123") },
  { label: "WOOD 124", swatchUrl: sw("6D", "WOOD-124") },
]

const OAK_PORE_SWATCHES = [
  { label: "OAK PORE CHT111",        swatchUrl: sw("8D", "OAK-PORE-CHT111") },
  { label: "OAK PORE S1005-Y20R",    swatchUrl: sw("8D", "OAK-PORE-S1005-Y20R") },
  { label: "OAK PORE S2010-B50G",    swatchUrl: sw("8D", "OAK-PORE-S2010-B50G") },
]

/**
 * Price group options for the model/finish selector.
 * Each code (e.g. "1M") maps to SKU prefixes via SKU_TO_PRICE_GROUP.
 * `scope` controls where the option appears: "box" (default product modes)
 * or "special" (Ειδικά picker, see COLOR_FAMILIES specials list).
 */
export const PRICE_GROUP_OPTIONS = [
  { code: "1M",  label: "1M - MFC Melamine 18mm",          scope: "box" },
  { code: "2P",  label: "2P - MFC + PVC Edge 18mm",        scope: "box" },
  { code: "3S",  label: "3S - Synchron Effect",            scope: "box" },
  { code: "4C",  label: "4C - Synchron 278cm",             scope: "box" },
  { code: "5L",  label: "5L - MDF Satin Lacquer 19mm",     scope: "box" },
  { code: "6D",  label: "6D - MDF Veneer Oak 20mm",        scope: "box" },
  { code: "6L",  label: "6L - MDF Lacquer Satin 19-22mm",  scope: "box" },
  { code: "7G",  label: "7G - MDF Gloss Lacquer 19mm",     scope: "box" },
  { code: "7T",  label: "7T - MDF Special Frames 22mm",    scope: "box" },
  { code: "8D",  label: "8D - Semi-Solid Oak 22mm",        scope: "box" },
  { code: "8G",  label: "8G - Semi-Solid Oak Gloss 22mm",  scope: "box" },
  { code: "9H",  label: "9H - Solid Oak Faltz 22mm",       scope: "box" },
  { code: "9M",  label: "9M - Solid Oak 22mm",             scope: "box" },
  { code: "10A", label: "10A - Aluminum + Glass 20mm",     scope: "box" },
  // Specials (Ειδικά) — share colors with the matching box family via COLOR_FAMILIES
  { code: "E1",  label: "E1 - Ειδικά (Melamine)",          scope: "special" },
  { code: "E2",  label: "E2 - Ειδικά (PVC Edge)",          scope: "special" },
  { code: "E3",  label: "E3 - Ειδικά (Synchron)",          scope: "special" },
  { code: "E4",  label: "E4 - Ειδικά (Synchron 278)",      scope: "special" },
  { code: "E5",  label: "E5 - Ειδικά (Satin Lacquer)",     scope: "special" },
  { code: "E6",  label: "E6 - Ειδικά (Veneer Oak)",        scope: "special" },
  { code: "E7",  label: "E7 - Ειδικά (Gloss Lacquer)",     scope: "special" },
  // Worktops (Πάγκοι) — Bakelite has its own palette (NOT shared with E1-E3 per customer).
  // Lustre worktops re-use the E5/E6/E7 codes above (truly shared swatch via COLOR_FAMILIES).
  { code: "P1",  label: "P1 - Πάγκος Βακελίτης 1",         scope: "worktop" },
  { code: "P2",  label: "P2 - Πάγκος Βακελίτης 2",         scope: "worktop" },
  { code: "P3",  label: "P3 - Πάγκος Βακελίτης 3",         scope: "worktop" },
]

/**
 * Split a flat swatches array into { colors, models } based on label prefix.
 * Any entry whose label starts with "MODEL " (case-insensitive) is a door-model swatch.
 * Everything else is a color swatch.
 */
export const splitSwatches = (entries = []) => {
  const colors = []
  const models = []
  for (const s of entries) {
    const isModel = typeof s?.label === "string" && /^model\b/i.test(s.label.trim())
    ;(isModel ? models : colors).push(s)
  }
  return { colors, models }
}

/**
 * Backward-compat accessor: returns the flat swatches array for a group.
 * New code should read `.colors` and `.models` directly from PRICE_GROUP_SWATCHES[code].
 */
export const getFlatSwatches = (pgCode) => {
  const g = PRICE_GROUP_SWATCHES[pgCode]
  if (!g) return []
  if (Array.isArray(g.swatches)) return g.swatches
  return [...(g.models || []), ...(g.colors || [])]
}

/**
 * Swatch images per price group, organized by ΚΑΤΗΓΟΡΙΑ ΧΡΕΩΣΗΣ.
 * Each entry has: label (display name), swatchUrl (FlexFlow public URL).
 * Shared palette groups (6L, 7G, 7T, 8G, 9H, 9M) reference their source group's swatches.
 *
 * Structure per group:
 *   {
 *     label: string,
 *     colors: [{ label, swatchUrl, hexColor? }, ...],   // color samples
 *     models: [{ label, swatchUrl }, ...],              // door-model shapes (label starts with "MODEL ")
 *     get swatches()                                    // legacy flat list, kept for back-compat
 *   }
 *
 * FALLBACK DATA: useDynamicCatalog.js loads swatches from the API at runtime.
 * This static data is only used when the API call fails.
 */
export const PRICE_GROUP_SWATCHES = {
  "1M": {
    label: "MFC Melamine 18mm",
    swatches: [
      { label: "8302 ELVEZIA GREY",      swatchUrl: sw("1M", "8302-ELVEZIA-GREY") },
      { label: "8502 ELVEZIA ROVERE",     swatchUrl: sw("1M", "8502-ELVEZIA-ROVERE") },
      { label: "988 ANOIXTO ΜΠΕΖ BEIGE",  swatchUrl: sw("1M", "988-ANOIXTO-BEIGE") },
      { label: "D 207 STONE GREY",        swatchUrl: sw("1M", "D-207-STONE-GREY") },
      { label: "D 143 GRAPHITE",          swatchUrl: sw("1M", "D-143-GRAPHITE") },
      { label: "F 238 FABRIC",            swatchUrl: sw("1M", "F-238-FABRIC") },
      { label: "8702 BROOKLYN BROWN",     swatchUrl: sw("1M", "8702-BROOKLYN-BROWN") },
      { label: "8402 ELVEZIA DARK GREY",  swatchUrl: sw("1M", "8402-ELVEZIA-DARK-GREY") },
      { label: "503 LIGHT RUSTIC OAK",    swatchUrl: sw("1M", "503-LIGHT-RUSTIC-OAK") },
      { label: "2902 WHITE LINEAR OAK",   swatchUrl: sw("1M", "2902-WHITE-LINEAR-OAK") },
      { label: "7102 ENERGY PINE BEIGE",  swatchUrl: sw("1M", "7102-ENERGY-PINE-BEIGE") },
      { label: "*60U",                    swatchUrl: sw("1M", "60U") },
      { label: "*5AE",                    swatchUrl: sw("1M", "5AE") },
      { label: "**W 908 WHITE ST7",       swatchUrl: sw("1M", "W-908-WHITE-ST7") },
      { label: "IN 494 LAPIS",            swatchUrl: sw("1M", "IN-494-LAPIS") },
      { label: "IN 074 GRAFHITE",         swatchUrl: sw("1M", "IN-074-GRAFHITE") },
      { label: "IN 090 WHITE",            swatchUrl: sw("1M", "IN-090-WHITE") },
      { label: "IN 988 SABBIA",           swatchUrl: sw("1M", "IN-988-SABBIA") },
    ],
  },
  "2P": {
    label: "MFC + PVC Edge 18mm",
    swatches: [
      { label: "U 732 DUST GREY",     swatchUrl: sw("2P", "U-732-DUST-GREY") },
      { label: "3011 RADA",           swatchUrl: sw("2P", "3011-RADA") },
      { label: "U 708 LIGHT GREY",    swatchUrl: sw("2P", "U-708-LIGHT-GREY") },
      { label: "2010 TERA",           swatchUrl: sw("2P", "2010-TERA") },
      { label: "2011 TERA",           swatchUrl: sw("2P", "2011-TERA") },
      { label: "70 TERA",             swatchUrl: sw("2P", "70-TERA") },
      { label: "74 TERA",             swatchUrl: sw("2P", "74-TERA") },
      { label: "494 TERA",            swatchUrl: sw("2P", "494-TERA") },
      { label: "694 TERA",            swatchUrl: sw("2P", "694-TERA") },
      { label: "3010 RADA",           swatchUrl: sw("2P", "3010-RADA") },
      { label: "6010 WHITE GLOSS",    swatchUrl: sw("2P", "6010-WHITE-GLOSS") },
      { label: "6015 BIANCO GLOSS",   swatchUrl: sw("2P", "6015-BIANCO-GLOSS") },
      { label: "6027 VISION GLOSS",   swatchUrl: sw("2P", "6027-VISION-GLOSS") },
      { label: "6035 GREY GLOSS",     swatchUrl: sw("2P", "6035-GREY-GLOSS") },
      { label: "6040 BLACK GLOSS",    swatchUrl: sw("2P", "6040-BLACK-GLOSS") },
      { label: "6080 BORDEAUX GLOSS", swatchUrl: sw("2P", "6080-BORDEAUX-GLOSS") },
      { label: "6025 CAPPUCCINO",     swatchUrl: sw("2P", "6025-CAPPUCCINO") },
      { label: "S 010 WHITE MATT",    swatchUrl: sw("2P", "S-010-WHITE-MATT") },
      { label: "S 020 CREAM MATT",    swatchUrl: sw("2P", "S-020-CREAM-MATT") },
      { label: "S 036 GRAY MATT",     swatchUrl: sw("2P", "S-036-GRAY-MATT") },
      { label: "S 040 BLACK MATT",    swatchUrl: sw("2P", "S-040-BLACK-MATT") },
      { label: "S 027 MINK MATT",     swatchUrl: sw("2P", "S-027-MINK-MATT") },
      { label: "S 028 MATT",          swatchUrl: sw("2P", "S-028-MATT") },
    ],
  },
  "3S": {
    label: "Synchron Effect",
    swatches: [
      { label: "C5",                swatchUrl: sw("3S", "C5") },
      { label: "C6",                swatchUrl: sw("3S", "C6") },
      { label: "C7",                swatchUrl: sw("3S", "C7") },
      { label: "OL 00",             swatchUrl: sw("3S", "OL-00") },
      { label: "OL 04",             swatchUrl: sw("3S", "OL-04") },
      { label: "3190",              swatchUrl: sw("3S", "3190") },
      { label: "FA 44 PENELOPE",    swatchUrl: sw("3S", "FA-44-PENELOPE") },
      { label: "FB 70 ARES",        swatchUrl: sw("3S", "FB-70-ARES") },
      { label: "FB 11 ARES",        swatchUrl: sw("3S", "FB-11-ARES") },
      { label: "UB 07 PRIMOFIORE",  swatchUrl: sw("3S", "UB-07-PRIMOFIORE") },
      { label: "UA 80 PRIMOFIORE",  swatchUrl: sw("3S", "UA-80-PRIMOFIORE") },
      { label: "UA 92 PRIMOFIORE",  swatchUrl: sw("3S", "UA-92-PRIMOFIORE") },
      { label: "S 133 ESPERIA",     swatchUrl: sw("3S", "S-133-ESPERIA") },
      { label: "J004 JUNGLE",       swatchUrl: sw("3S", "J004-JUNGLE") },
      { label: "IT 05 ROMA",        swatchUrl: sw("3S", "IT-05-ROMA") },
    ],
  },
  "4C": {
    label: "Synchron 278cm",
    swatches: [
      { label: "LR 71 RIGA", swatchUrl: sw("4C", "LR-71-RIGA") },
      { label: "FB 80 RIGA", swatchUrl: sw("4C", "FB-80-RIGA") },
    ],
  },
  "5L": {
    label: "MDF Satin Lacquer 19mm",
    swatches: [
      { label: "MODEL 501/19mm",  swatchUrl: sw("5L", "MODEL-501-19mm") },
      { label: "MODEL 519/19mm",  swatchUrl: sw("5L", "MODEL-519-19mm") },
      { label: "MODEL 538/19mm",  swatchUrl: sw("5L", "MODEL-538-19mm") },
      { label: "MODEL 590/19mm",  swatchUrl: sw("5L", "MODEL-590-19mm") },
      { label: "173 64",          swatchUrl: sw("5L", "173-64") },
      { label: "43001",           swatchUrl: sw("5L", "43001") },
      { label: "43004",           swatchUrl: sw("5L", "43004") },
      { label: "44 N5",           swatchUrl: sw("5L", "44-N5") },
      { label: "54 N3",           swatchUrl: sw("5L", "54-N3") },
      { label: "F 167",           swatchUrl: sw("5L", "F-167") },
      { label: "F 157",           swatchUrl: sw("5L", "F-157") },
      { label: "F 200",           swatchUrl: sw("5L", "F-200") },
      { label: "H 157",           swatchUrl: sw("5L", "H-157") },
      { label: "J 155",           swatchUrl: sw("5L", "J-155") },
      { label: "L 168",           swatchUrl: sw("5L", "L-168") },
      { label: "X 137",           swatchUrl: sw("5L", "X-137") },
      { label: "ΜΑΥΡΟ",           swatchUrl: sw("5L", "MAVRO") },
      { label: "ΛΕΥΚΟ",           swatchUrl: sw("5L", "LEFKO") },
      { label: "X 120",           swatchUrl: sw("5L", "X-120") },
      { label: "001W-3",          swatchUrl: sw("5L", "001W-3") },
      { label: "067 N1",          swatchUrl: sw("5L", "067-N1") },
      { label: "067 N2",          swatchUrl: sw("5L", "067-N2") },
      { label: "88 R5",           swatchUrl: sw("5L", "88-R5") },
      { label: "45 N3",           swatchUrl: sw("5L", "45-N3") },
      { label: "89 R6",           swatchUrl: sw("5L", "89-R6") },
      { label: "170 G6",          swatchUrl: sw("5L", "170-G6") },
      { label: "S 1005 Y20R",     swatchUrl: sw("5L", "S-1005-Y20R") },
    ],
  },
  "6D": {
    label: "MDF Veneer Oak 20mm",
    swatches: [
      { label: "MODEL 509/22mm",      swatchUrl: sw("6D", "MODEL-509-22mm") },
      { label: "MODEL 593/22mm",      swatchUrl: sw("6D", "MODEL-593-22mm") },
      { label: "MODEL 529/22mm",      swatchUrl: sw("6D", "MODEL-529-22mm") },
      { label: "MODEL 570/19mm",      swatchUrl: sw("6D", "MODEL-570-19mm") },
      { label: "MODEL 562/19mm",      swatchUrl: sw("6D", "MODEL-562-19mm") },
      { label: "MODEL 521/22mm",      swatchUrl: sw("6D", "MODEL-521-22mm") },
      { label: "MODEL 561/19mm",      swatchUrl: sw("6D", "MODEL-561-19mm") },
      { label: "MODEL 571/19mm",      swatchUrl: sw("6D", "MODEL-571-19mm") },
      { label: "MODEL 572-1/19mm",    swatchUrl: sw("6D", "MODEL-572-1-19mm") },
      { label: "MODEL 572-7/19mm",    swatchUrl: sw("6D", "MODEL-572-7-19mm") },
      { label: "MODEL 530/22mm",      swatchUrl: sw("6D", "MODEL-530-22mm") },
      { label: "MODEL 519/20mm",      swatchUrl: sw("6D", "MODEL-519-20mm") },
      { label: "MODEL 520/20mm",      swatchUrl: sw("6D", "MODEL-520-20mm") },
      { label: "MODEL 590/20mm",      swatchUrl: sw("6D", "MODEL-590-20mm") },
      { label: "WOOD 101",            swatchUrl: sw("6D", "WOOD-101") },
      { label: "WOOD 102",            swatchUrl: sw("6D", "WOOD-102") },
      { label: "WOOD 103",            swatchUrl: sw("6D", "WOOD-103") },
      { label: "WOOD 104",            swatchUrl: sw("6D", "WOOD-104") },
      { label: "WOOD 105",            swatchUrl: sw("6D", "WOOD-105") },
      { label: "WOOD 106",            swatchUrl: sw("6D", "WOOD-106") },
      { label: "WOOD 107",            swatchUrl: sw("6D", "WOOD-107") },
      { label: "WOOD 108",            swatchUrl: sw("6D", "WOOD-108") },
      { label: "WOOD 109",            swatchUrl: sw("6D", "WOOD-109") },
      { label: "WOOD 110",            swatchUrl: sw("6D", "WOOD-110") },
      { label: "WOOD 111",            swatchUrl: sw("6D", "WOOD-111") },
      { label: "WOOD 112",            swatchUrl: sw("6D", "WOOD-112") },
      { label: "WOOD 113",            swatchUrl: sw("6D", "WOOD-113") },
      { label: "WOOD 114",            swatchUrl: sw("6D", "WOOD-114") },
      { label: "WOOD 115",            swatchUrl: sw("6D", "WOOD-115") },
      { label: "WOOD 116",            swatchUrl: sw("6D", "WOOD-116") },
      { label: "WOOD 117",            swatchUrl: sw("6D", "WOOD-117") },
      { label: "WOOD 118",            swatchUrl: sw("6D", "WOOD-118") },
      { label: "WOOD 119",            swatchUrl: sw("6D", "WOOD-119") },
      { label: "WOOD 120",            swatchUrl: sw("6D", "WOOD-120") },
      { label: "WOOD 121",            swatchUrl: sw("6D", "WOOD-121") },
      { label: "WOOD 122",            swatchUrl: sw("6D", "WOOD-122") },
      { label: "WOOD 123",            swatchUrl: sw("6D", "WOOD-123") },
      { label: "WOOD 124",            swatchUrl: sw("6D", "WOOD-124") },
    ],
  },
  // 6L shares 5L lacquer palette
  "6L": { label: "MDF Lacquer Satin 19-22mm", get swatches() { return PRICE_GROUP_SWATCHES["5L"].swatches } },
  "7T": {
    label: "MDF Special Frames 22mm",
    swatches: [
      { label: "MODEL 558-1/22mm",    swatchUrl: sw("7T", "MODEL-558-1-22mm") },
      { label: "MODEL 574-1/22mm",    swatchUrl: sw("7T", "MODEL-574-1-22mm") },
      { label: "MODEL 574-X/22mm",    swatchUrl: sw("7T", "MODEL-574-X-22mm") },
      { label: "MODEL 574-XG/22mm",   swatchUrl: sw("7T", "MODEL-574-XG-22mm") },
      ...LACQUER_COLORS,
    ],
  },
  "7G": {
    label: "MDF Gloss Lacquer 19mm",
    swatches: [
      { label: "MODEL 518/19mm",  swatchUrl: sw("7G", "MODEL-518-19mm") },
      { label: "MODEL 519/19mm",  swatchUrl: sw("7G", "MODEL-519-19mm") },
      { label: "MODEL 590/19mm",  swatchUrl: sw("7G", "MODEL-590-19mm") },
      ...LACQUER_COLORS,
    ],
  },
  "8D": {
    label: "Semi-Solid Oak 22mm",
    swatches: [
      { label: "MODEL 651-1/22mm",       swatchUrl: sw("8D", "MODEL-651-1-22mm") },
      { label: "DCP-WHITE",              swatchUrl: sw("8D", "DCP-WHITE") },
      { label: "DCP 43032/122",          swatchUrl: sw("8D", "DCP-43032-122") },
      { label: "DCP 43030/123",          swatchUrl: sw("8D", "DCP-43030-123") },
      { label: "DCP MIL09/103",          swatchUrl: sw("8D", "DCP-MIL09-103") },
      { label: "DCP 43004/198",          swatchUrl: sw("8D", "DCP-43004-198") },
      { label: "MODEL 601-2/22mm",       swatchUrl: sw("8D", "MODEL-601-2-22mm") },
      { label: "MODEL 602-2/22mm",       swatchUrl: sw("8D", "MODEL-602-2-22mm") },
      { label: "MODEL 631-2/22mm",       swatchUrl: sw("8D", "MODEL-631-2-22mm") },
      { label: "OAK PORE CHT111",        swatchUrl: sw("8D", "OAK-PORE-CHT111") },
      { label: "OAK PORE S1005-Y20R",    swatchUrl: sw("8D", "OAK-PORE-S1005-Y20R") },
      { label: "BEECH S0520-G40Y",       swatchUrl: sw("8D", "BEECH-S0520-G40Y") },
      { label: "BEECH S2010-B50G",       swatchUrl: sw("8D", "BEECH-S2010-B50G") },
      { label: "MODEL 652-1/22mm",       swatchUrl: sw("8D", "MODEL-652-1-22mm") },
      { label: "MODEL 671-1/22mm",       swatchUrl: sw("8D", "MODEL-671-1-22mm") },
      { label: "MODEL 658-1/22mm",       swatchUrl: sw("8D", "MODEL-658-1-22mm") },
      { label: "MODEL 672-1/22mm",       swatchUrl: sw("8D", "MODEL-672-1-22mm") },
      { label: "MODEL 661-1/22mm",       swatchUrl: sw("8D", "MODEL-661-1-22mm") },
      { label: "MODEL 662-1",            swatchUrl: sw("8D", "MODEL-662-1") },
      { label: "MODEL 672-7",            swatchUrl: sw("8D", "MODEL-672-7") },
      { label: "MODEL 654-1",            swatchUrl: sw("8D", "MODEL-654-1") },
      { label: "MODEL 655-1",            swatchUrl: sw("8D", "MODEL-655-1") },
      { label: "ΛΕΥΚΟ ΜΕ ΠΑΤΙΝΑ ΧΑΛΚΟΥ", swatchUrl: sw("8D", "LEFKO-PATINA-CHALKOU") },
      { label: "RAL1019 ΜΟΚΑ",           swatchUrl: sw("8D", "RAL1019-MOKA") },
      { label: "S3030-Y60R ΡΟΔΙ",        swatchUrl: sw("8D", "S3030-Y60R-RODI") },
      { label: "MIL009 ΒΑΝΙΛΙΑ",         swatchUrl: sw("8D", "MIL009-VANILIA") },
      { label: "MODEL 522 ΤΑΜΠΛΑΔΩΤΟ",   swatchUrl: sw("8D", "MODEL-522-TAMPLADOTO") },
    ],
  },
  "8G": {
    label: "Semi-Solid Oak Gloss 22mm",
    swatches: [
      { label: "MODEL 509/22mm",  swatchUrl: sw("8G", "MODEL-509-22mm") },
      { label: "MODEL 593/22mm",  swatchUrl: sw("8G", "MODEL-593-22mm") },
      { label: "MODEL 529/22mm",  swatchUrl: sw("8G", "MODEL-529-22mm") },
      { label: "MODEL 521/22mm",  swatchUrl: sw("8G", "MODEL-521-22mm") },
      { label: "MODEL 530/22mm",  swatchUrl: sw("8G", "MODEL-530-22mm") },
      ...LACQUER_COLORS,
    ],
  },
  "9H": {
    label: "Solid Oak Faltz 22mm",
    swatches: [
      { label: "MODEL 743-1", swatchUrl: sw("9H", "MODEL-743-1") },
      { label: "MODEL 744-1", swatchUrl: sw("9H", "MODEL-744-1") },
      { label: "MODEL 656-1", swatchUrl: sw("9H", "MODEL-656-1") },
      ...WOOD_SWATCHES,
      ...OAK_PORE_SWATCHES,
    ],
  },
  "9M": {
    label: "Solid Oak 22mm",
    swatches: [
      { label: "MODEL 801-5", swatchUrl: sw("9M", "MODEL-801-5") },
      { label: "MODEL 831-5", swatchUrl: sw("9M", "MODEL-831-5") },
      { label: "MODEL 861-5", swatchUrl: sw("9M", "MODEL-861-5") },
      ...WOOD_SWATCHES,
      ...OAK_PORE_SWATCHES,
    ],
  },
  "10A": {
    label: "Aluminum + Glass 20mm",
    swatches: [
      { label: "9003 ΛΕΥΚΟ",          swatchUrl: sw("10A", "9003") },
      { label: "9005 ΜΑΥΡΟ",          swatchUrl: sw("10A", "9005") },
      { label: "1236 ΜΟΚΑ",           swatchUrl: sw("10A", "1236") },
      { label: "8017 ΚΑΦΕ ΣΚΟΥΡΟ",    swatchUrl: sw("10A", "8017") },
      { label: "8715",                swatchUrl: sw("10A", "8715") },
      { label: "1015 ΜΠΕΖ",          swatchUrl: sw("10A", "1015") },
      { label: "9015 ΚΑΦΕ",          swatchUrl: sw("10A", "9015") },
      { label: "7035",               swatchUrl: sw("10A", "7035") },
      { label: "9006 ΑΣΗΜΙ",         swatchUrl: sw("10A", "9006") },
      { label: "7000",               swatchUrl: sw("10A", "7000") },
      { label: "ΦΙΜΕ",               swatchUrl: sw("10A", "FIME") },
      { label: "MODEL 903-1 ΣΑΤΙΝΕ",  swatchUrl: sw("10A", "MODEL-903-1-SATINE") },
      { label: "MODEL 903-1 ΦΙΜΕ",    swatchUrl: sw("10A", "MODEL-903-1-FIME") },
      { label: "MODEL 903-1 ΤΖΑΜΙ",   swatchUrl: sw("10A", "MODEL-903-1-TZAMI") },
    ],
  },
}

// Post-process: derive `colors` and `models` arrays from each group's `swatches`.
// Lets consumers read PRICE_GROUP_SWATCHES[code].colors / .models without
// changing the source-of-truth data shape above.
for (const code of Object.keys(PRICE_GROUP_SWATCHES)) {
  const group = PRICE_GROUP_SWATCHES[code]
  // 6L uses a getter that proxies to 5L.swatches — must use a getter here too
  // so the derived arrays stay in sync with the upstream group.
  const desc = Object.getOwnPropertyDescriptor(group, "swatches")
  if (desc && typeof desc.get === "function") {
    Object.defineProperty(group, "colors", {
      enumerable: true,
      get() {
        return splitSwatches(this.swatches).colors
      },
    })
    Object.defineProperty(group, "models", {
      enumerable: true,
      get() {
        return splitSwatches(this.swatches).models
      },
    })
  } else {
    const { colors, models } = splitSwatches(group.swatches || [])
    group.colors = colors
    group.models = models
  }
}
